import { useVirtualizer } from '@tanstack/react-virtual';
import { type ComponentPropsWithoutRef, memo, type ReactNode, useRef } from 'react';

import { cn } from '@air/utils-tailwind';

export type WorkspaceSidebarVirtualizedListProps<Data> = ComponentPropsWithoutRef<'div'> & {
  items: Data[];
  renderItem: ({ index, item }: { index: number; item: Data }) => ReactNode;

  /**
   * Make sure that the scroll element is handled via `useState` or another method that
   * re-renders the component when the scroll element changes.
   */
  scrollElement: HTMLDivElement | HTMLElement | null;
};

const _WorkspaceSidebarVirtualizedList = <Data,>({
  className,
  items,
  renderItem,
  scrollElement,
  style,
  ...restOfProps
}: WorkspaceSidebarVirtualizedListProps<Data>) => {
  const listRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => 32,
    overscan: 10,
    scrollMargin: listRef.current?.offsetTop ?? 0,
  });

  return (
    <div
      className={cn('relative w-full shrink-0', className)}
      style={{
        ...style,
        height: virtualizer.getTotalSize(),
      }}
      {...restOfProps}
      ref={listRef}
    >
      <div
        className="absolute top-0 left-0 flex w-full flex-col"
        style={{
          transform: `translateY(${
            (virtualizer.getVirtualItems()[0]?.start ?? 0) - virtualizer.options.scrollMargin
          }px)`,
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const item = items[virtualRow.index];

          return (
            <div
              className="relative"
              data-index={virtualRow.index}
              key={virtualRow.key}
              ref={virtualizer.measureElement}
            >
              {renderItem({ index: virtualRow.index, item })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const WorkspaceSidebarVirtualizedList = memo(
  _WorkspaceSidebarVirtualizedList,
) as typeof _WorkspaceSidebarVirtualizedList;
